import React from 'react';
import { Link } from 'react-router-dom';
import platformImage from '../assests/main image.png';

const platformFeatures = [
  'WhatsApp Business API',
  'Customer messaging',
  'Message templates',
  'Marketing campaigns',
  'Automated notifications',
  'Customer CRM',
  'Lead management',
  'Chatbot workflows',
  'Media messaging',
  'Webhooks',
  'Developer APIs',
  'Message status tracking',
];

const messageTypes = ['Text', 'Images', 'Videos', 'Documents', 'Audio', 'Approved message templates'];
const templateExamples = ['Order updates', 'Appointment reminders', 'Customer notifications', 'Authentication', 'Marketing campaigns', 'Service updates'];
const campaignExamples = ['Product launches', 'Special offers', 'New services', 'Seasonal promotions', 'Customer re-engagement', 'Event announcements', 'Product recommendations'];
const crmRecords = ['Customers', 'Leads', 'Tags', 'Customer status', 'Contact information', 'Appointments', 'Feedback', 'Communication workflows'];
const automationTasks = ['Welcome messages', 'Notifications', 'Appointment reminders', 'Customer follow-ups', 'Campaigns', 'Service updates', 'Lead communication', 'Customer feedback requests'];
const integrations = ['CRM systems', 'Ecommerce platforms', 'SaaS applications', 'Websites', 'Mobile applications', 'ERP systems', 'Appointment platforms', 'Customer support systems', 'Custom applications'];
const saasUseCases = ['Customer notifications', 'Appointment management', 'Transaction updates', 'Authentication', 'Customer support', 'Marketing', 'Automated reminders', 'Business alerts'];

const developerCapabilities = [
  ['Messaging', 'Send supported WhatsApp messages from your application.'],
  ['Templates', 'Use approved templates for supported business communication.'],
  ['Media', 'Build workflows around images, videos, documents, and audio.'],
  ['Webhooks', 'Receive messaging events and integrate them with your backend.'],
  ['Automation', 'Trigger WhatsApp communication from application events.'],
  ['CRM', 'Connect customer information with WhatsApp communication.'],
];

const agencyIndustries = ['Ecommerce', 'Education', 'Real estate', 'Healthcare businesses where permitted by applicable policies and laws', 'Professional services', 'Retail', 'Hospitality', 'Service businesses', 'SaaS companies'];
const platformBenefits = [
  ['Centralized Management', 'Manage your WhatsApp communication from one place.'],
  ['API Integration', 'Connect WhatsApp with your existing applications.'],
  ['Campaign Management', 'Create and manage targeted WhatsApp campaigns.'],
  ['Automation', 'Reduce repetitive messaging with automated workflows.'],
  ['CRM', 'Organize customers and leads alongside your communication.'],
  ['Developer Tools', 'Build custom WhatsApp workflows using APIs and webhooks.'],
  ['Scalable Architecture', 'Build communication workflows that can grow with your business.'],
];

const setupSteps = [
  ['Connect Your WhatsApp Business Account', 'Connect your WhatsApp Business infrastructure with the platform.'],
  ['Configure Your Business', 'Set up your business profile, messaging configuration, and required integrations.'],
  ['Create Message Templates', 'Create templates for the communication workflows your business needs.'],
  ['Connect Your Application', 'Use APIs and webhooks to connect your website, CRM, SaaS application, or internal system.'],
  ['Automate Communication', 'Trigger WhatsApp messages based on customer actions and business events.'],
  ['Manage Customer Communication', 'Use campaigns, CRM, and messaging workflows to manage customer engagement.'],
];

const industryUseCases = [
  ['Ecommerce', 'Send order confirmations, shipping updates, delivery notifications, and customer messages.'],
  ['Education', 'Communicate enrollment information, reminders, announcements, and student updates.'],
  ['Real Estate', 'Follow up with leads, schedule appointments, and send property-related communication.'],
  ['Travel & Hospitality', 'Send booking confirmations, reminders, and service updates.'],
  ['Professional Services', 'Manage appointments, customer notifications, and follow-ups.'],
  ['SaaS', 'Integrate WhatsApp directly into your software workflows.'],
  ['Agencies', 'Build WhatsApp communication solutions for multiple business clients.'],
];

const customerJourney = [
  ['Acquire', 'Connect with new leads and customers.'],
  ['Engage', 'Send relevant business communication.'],
  ['Convert', 'Follow up with customers and support sales workflows.'],
  ['Notify', 'Keep customers informed about important events.'],
  ['Support', 'Help customers through WhatsApp conversations.'],
  ['Retain', 'Use relevant updates and customer engagement workflows.'],
];

const faqs = [
  ['What is WhatsApp Business API?', 'WhatsApp Business API is a business communication interface that allows companies to integrate WhatsApp messaging with applications, systems, and business workflows.'],
  ['What can I use WhatsApp Business API for?', 'Businesses can use WhatsApp API for customer notifications, authentication, appointment reminders, marketing communication, support workflows, order updates, and other permitted business messaging use cases.'],
  ['Can I integrate WhatsApp API with my CRM?', 'Yes. HAWKTRION is designed to help businesses connect WhatsApp messaging with CRM and customer-management workflows.'],
  ['Can developers integrate WhatsApp API with custom applications?', 'Yes. Developers can use API and webhook-based integrations to connect WhatsApp communication with websites, SaaS applications, and custom software.'],
  ['Can I send marketing messages through WhatsApp?', 'Businesses can use WhatsApp for permitted marketing communication, subject to applicable WhatsApp policies, user consent, and approved messaging templates where required. Obtain appropriate opt-in and respect user opt-out requests.'],
  ['Can WhatsApp messages be automated?', 'Yes. Businesses can automate permitted WhatsApp communication using application events, workflows, templates, and APIs.'],
  ['Can I send messages without a template?', 'When a customer messages a business, businesses can respond within the applicable customer-service window without using a template. Outside that window, WhatsApp requires approved templates for business-initiated messages.'],
  ['Is HAWKTRION suitable for SaaS platforms?', 'Yes. HAWKTRION can be used as a WhatsApp communication layer for SaaS products and custom applications.'],
  ['Can I integrate WhatsApp with my website?', 'Yes. Your website or backend application can connect to WhatsApp through API-based integration and trigger supported business communication.'],
  ['Does WhatsApp have messaging policies?', 'Yes. Businesses using the WhatsApp Business Platform must comply with WhatsApp Business policies, messaging rules, and applicable laws. Businesses are also responsible for obtaining required permissions and consent for customer communication.'],
];



const BulletList = ({ items }) => (
  <ul className="business-api-list">
    {items.map((item) => <li key={item}>{item}</li>)}
  </ul>
);

const SectionHeading = ({ eyebrow, title, children }) => (
  <div className="business-api-heading">
    {eyebrow && <div className="sec-label"><span className="sec-label-dot"></span>{eyebrow}</div>}
    <h2>{title}</h2>
    {children && <p>{children}</p>}
  </div>
);

const CardGrid = ({ items, className = '' }) => (
  <div className={`business-api-grid ${className}`}>
    {items.map(([title, description]) => (
      <article className="business-api-card" key={title}>
        <h3>{title}</h3>
        <p>{description}</p>
      </article>
    ))}
  </div>
);

const Workflow = ({ steps }) => (
  <ol className="business-api-workflow">
    {steps.map((step, index) => (
      <li key={step}>
        <span className="business-api-step-number">{index + 1}</span>
        <span>{step}</span>
      </li>
    ))}
  </ol>
);

const WhatsappBusinessApiContent = () => (
  <div className="business-api-content">
    <section className="business-api-section business-api-intro">
      <div className="container">
        <div className="business-api-intro-grid">
          <div className="business-api-intro-copy">
            <p className="business-api-lead">Connect your business with customers on WhatsApp using HAWKTRION&apos;s WhatsApp Business API platform.</p>
            <p>Send business messages, manage WhatsApp templates, automate customer notifications, run campaigns, organize contacts, and integrate WhatsApp with your CRM, SaaS platform, or custom application.</p>
            <div className="business-api-actions">
              <Link to="/signup" className="btn btn-p">Get Started</Link>
              <Link to="/whatsapp-api-documentation/" className="btn btn-s">Explore API Documentation</Link>
            </div>
          </div>
          <figure className="business-api-visual">
            <img src={platformImage} alt="HAWKTRION WhatsApp Business platform overview showing messaging, CRM, automation, campaigns, integrations, and analytics" />
            <figcaption>One platform for messaging, automation, customer management, and integrations.</figcaption>
          </figure>
        </div>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Connected Communication" title="Connect Your Business to WhatsApp">
          WhatsApp is one of the most convenient channels for customers to communicate with businesses. HAWKTRION helps businesses turn WhatsApp into a scalable communication channel by connecting business workflows with the WhatsApp Business Platform.
        </SectionHeading>
        <p className="business-api-copy">From customer notifications and appointment reminders to marketing campaigns and support conversations, manage WhatsApp communication from one centralized platform.</p>
        <h3 className="business-api-subheading">One Platform for Business Messaging</h3>
        <BulletList items={platformFeatures} />
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="The Platform" title="What Is WhatsApp Business API?">
          WhatsApp Business API enables businesses to integrate WhatsApp communication with their applications, systems, and customer workflows. Unlike a basic WhatsApp Business application, an API-based solution is designed for businesses that need to connect WhatsApp with software, automate communication, and manage messaging at scale.
        </SectionHeading>
        <p className="business-api-copy">HAWKTRION provides a business-focused platform around WhatsApp API capabilities so your team can manage communication without building every messaging workflow from scratch.</p>
        <h3 className="business-api-subheading">Connect Your Existing Systems</h3>
        <p className="business-api-copy">Your application can trigger WhatsApp communication based on business events:</p>
        <Workflow steps={['Customer places an order', 'Your application processes the order', 'HAWKTRION sends the WhatsApp message', 'Customer receives the update']} />
        <p className="business-api-copy">This approach allows WhatsApp to become part of your existing business workflow.</p>
        <div className="business-api-example">
          <div className="business-api-example-event">
            <span className="business-api-example-label">APPLICATION EVENT</span>
            <strong>order.paid</strong>
            <p>Payment confirmed for order #4821</p>
          </div>
          <div className="business-api-example-arrow" aria-hidden="true">→</div>
          <div className="business-api-example-chat">
            <div className="business-api-example-chat-head">
              <span className="business-api-chat-avatar">H</span>
              <span><strong>HAWKTRION Store</strong><small>WhatsApp Business</small></span>
              <span className="business-api-chat-status">Delivered</span>
            </div>
            <p className="business-api-chat-bubble">Hi Aisha, we&apos;ve received your payment for order #4821. We&apos;ll let you know as soon as it ships.</p>
            <p className="business-api-chat-meta">Template: order_confirmation · just now</p>
          </div>
        </div>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Platform Capabilities" title="WhatsApp Business API Features" />
        <div className="business-api-feature-grid">
          <article className="business-api-card">
            <h3>Send WhatsApp Messages</h3>
            <p>Communicate with customers through supported business messaging workflows. Send different types of content:</p>
            <BulletList items={messageTypes} />
            <p>Create communication workflows that match your business requirements.</p>
          </article>
          <article className="business-api-card">
            <h3>WhatsApp Message Templates</h3>
            <p>Create and manage message templates for business-initiated communication.</p>
            <BulletList items={templateExamples} />
            <p>HAWKTRION helps organize templates in one centralized platform. Business-initiated conversations require approved templates and remain subject to applicable WhatsApp policies.</p>
          </article>
          <article className="business-api-card">
            <h3>WhatsApp Marketing Campaigns</h3>
            <p>Reach customers with targeted campaigns for launches, offers, new services, seasonal promotions, re-engagement, events, and product recommendations.</p>
            <p>Organize your customer audience and create campaigns around relevant business communication.</p>
            <Link to="/whatsapp-marketing/" className="business-api-text-link">Create a WhatsApp Campaign</Link>
          </article>
          <article className="business-api-card">
            <h3>Automated WhatsApp Notifications</h3>
            <p>Keep customers informed automatically when important events happen in your application.</p>
            <div className="business-api-event-list">
              <p><strong>Order created</strong><span>Send an order confirmation.</span></p>
              <p><strong>Order shipped</strong><span>Send a delivery update.</span></p>
              <p><strong>Appointment booked</strong><span>Send an appointment confirmation.</span></p>
              <p><strong>Appointment approaching</strong><span>Send a reminder.</span></p>
              <p><strong>Payment or service updated</strong><span>Send an important customer update.</span></p>
            </div>
            <p>Automation can reduce repetitive manual communication and help keep customers informed.</p>
          </article>
        </div>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Customer Management" title="WhatsApp CRM">
          Customer communication becomes more useful when your team has access to customer information. HAWKTRION provides CRM-oriented features for organizing customer records and WhatsApp communication workflows.
        </SectionHeading>
        <BulletList items={crmRecords} />
        <h3 className="business-api-subheading">Turn Conversations Into Customer Journeys</h3>
        <Workflow steps={['New lead', 'WhatsApp follow-up', 'Customer qualification', 'Appointment', 'Customer notification', 'Customer feedback']} />
        <p className="business-api-copy">Keep customer communication organized throughout the journey.</p>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Workflow Automation" title="WhatsApp API Automation">
          Your team should not have to manually send the same messages again and again. Create automated WhatsApp workflows based on business events, customer actions, and scheduled activities.
        </SectionHeading>
        <BulletList items={automationTasks} />
        <h3 className="business-api-subheading">Build Event-Based Communication</h3>
        <Workflow steps={['Business event', 'Your application', 'HAWKTRION API', 'WhatsApp', 'Customer']} />
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Connect Your Stack" title="WhatsApp API Integration">
          Integrate WhatsApp into the software your business already uses.
        </SectionHeading>
        <BulletList items={integrations} />
        <h3 className="business-api-subheading">Developer-Friendly API</h3>
        <p className="business-api-copy">Developers can use APIs and webhooks to connect WhatsApp messaging with their application&apos;s business logic.</p>
        <Workflow steps={['Customer registration', 'Application event', 'HAWKTRION API', 'WhatsApp template', 'Customer']} />
        <p className="business-api-copy">Build WhatsApp communication directly into your existing product.</p>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="For SaaS Platforms" title="WhatsApp API for SaaS">
          If you operate a SaaS product, WhatsApp can become an important part of your customer communication infrastructure. Use HAWKTRION to build WhatsApp functionality into your SaaS workflows.
        </SectionHeading>
        <BulletList items={saasUseCases} />
        <p className="business-api-copy">Build WhatsApp communication around your application&apos;s existing events and workflows.</p>
        <Link to="/whatsapp-api-for-saas/" className="business-api-text-link">Build WhatsApp Into Your SaaS</Link>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="For Developers" title="Developer-Friendly WhatsApp Integration">
          HAWKTRION provides API-focused capabilities for developers building WhatsApp integrations.
        </SectionHeading>
        <CardGrid items={developerCapabilities} />
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="For Agencies" title="Build WhatsApp Solutions for Your Clients">
          Digital agencies can use HAWKTRION to build WhatsApp communication solutions for businesses across different industries.
        </SectionHeading>
        <BulletList items={agencyIndustries} />
        <p className="business-api-copy">Build repeatable WhatsApp solutions and customize communication workflows for each client.</p>
        <Link to="/whatsapp-api-for-agencies/" className="business-api-text-link">Build Client WhatsApp Solutions</Link>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Why HAWKTRION" title="WhatsApp Messaging + Automation + CRM + APIs">
          HAWKTRION brings important WhatsApp business capabilities together in one platform.
        </SectionHeading>
        <CardGrid items={platformBenefits} />
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Getting Started" title="How HAWKTRION WhatsApp API Works" />
        <ol className="business-api-steps">
          {setupSteps.map(([title, description], index) => (
            <li className="business-api-step" key={title}>
              <span className="business-api-step-number">{index + 1}</span>
              <div><h3>{title}</h3><p>{description}</p></div>
            </li>
          ))}
        </ol>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Across Industries" title="WhatsApp Business API Use Cases" />
        <CardGrid items={industryUseCases} />
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Customer Lifecycle" title="From First Message to Customer Retention">
          Use WhatsApp throughout the customer lifecycle.
        </SectionHeading>
        <CardGrid items={customerJourney} />
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Modern Business Communication" title="Built for Modern Business Communication" />
        <p className="business-api-copy">Whether you are a small business, growing company, digital agency, SaaS provider, or enterprise team, HAWKTRION helps connect WhatsApp with your business processes.</p>
        <p className="business-api-copy">Instead of managing separate systems for messaging, campaigns, customer data, and automation, create a connected WhatsApp communication workflow.</p>
      </div>
    </section>

    <section className="business-api-section business-api-faq" id="business-api-faq">
      <div className="container">
        <SectionHeading eyebrow="Answers" title="WhatsApp Business API FAQs" />
        <div className="business-api-faq-list">
          {faqs.map(([question, answer]) => (
            <details key={question}>
              <summary>{question}</summary>
              <p>{answer}</p>
            </details>
          ))}
        </div>
      </div>
    </section>

    <section className="business-api-section business-api-final-cta">
      <div className="container">
        <div className="business-api-cta-panel">
          <SectionHeading eyebrow="Get Started" title="Connect WhatsApp With Your Business Today">
            Bring messaging, automation, campaigns, CRM, and API integration together with HAWKTRION. Build better customer communication workflows and make WhatsApp a connected part of your business.
          </SectionHeading>
          <div className="business-api-actions">
            <Link to="/signup" className="btn btn-p">Get Started With HAWKTRION</Link>
            <Link to="/whatsapp-api-documentation/" className="btn btn-s">View WhatsApp API Documentation</Link>
          </div>
        </div>
       
      </div>
    </section>
  </div>
);

export default WhatsappBusinessApiContent;